import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { CepLocation, getStoreScheduleConfigApi, locateStoreByCepApi, saveStoreLocationApi } from "../../dp/api/scheduleApi";

const PRECISION_HINT: Record<CepLocation["precision"], string> = {
  NUMERO: "Ponto encontrado pelo número.",
  RUA: "O número não foi achado no mapa: o ponto está na rua. Confira no mapa e ajuste se a rua for longa.",
  BAIRRO: "Só o bairro foi achado no mapa. Confira no mapa e ajuste a latitude e a longitude.",
  CIDADE: "Este CEP só aponta a cidade. Ajuste a latitude e a longitude, ou use a localização atual estando na loja.",
};

const maskCep = (v: string) => {
  const d = v.replace(/\D/g, "").slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

type Props = {
  token: string;
  storeId: string;
};

/**
 * Aba "Posição" da ficha da loja: latitude, longitude e raio. É o raio que a
 * avaliação de loja confere quando `avaliacao.exigirLocalizacao` está ligada
 * (plan-notificacoes-whatsapp, 22/10/2026); as marcações antigas do ponto também
 * mostram "fora da loja" por ele. Salva pela rota só da posição, sem mexer no horário.
 */
export default function StoreLocationForm({ token, storeId }: Props) {
  const [loaded, setLoaded] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState("300");
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [cep, setCep] = useState("");
  const [numero, setNumero] = useState("");
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState<CepLocation | null>(null);

  useEffect(() => {
    let active = true;
    getStoreScheduleConfigApi(token, storeId)
      .then((c) => {
        if (!active) return;
        setLat(c.location ? String(c.location.latitude) : "");
        setLng(c.location ? String(c.location.longitude) : "");
        setRadius(String(c.location?.radiusMeters ?? 300));
        setHasSaved(!!c.location);
        setLoaded(true);
      })
      .catch((error: any) => {
        if (active) toast.error(error?.message || "Erro ao carregar a posição da loja.");
      });
    return () => {
      active = false;
    };
  }, [token, storeId]);

  if (!loaded) return <div className="dpEmpty">Carregando...</div>;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const latitude = Number(lat.replace(",", "."));
    const longitude = Number(lng.replace(",", "."));
    const radiusMeters = Math.round(Number(radius));
    if (!lat.trim() || !lng.trim()) return toast.error("Informe a latitude e a longitude da loja.");
    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90 || !Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
      return toast.error("Latitude ou longitude inválida.");
    }
    if (!Number.isFinite(radiusMeters) || radiusMeters < 50 || radiusMeters > 2000) return toast.error("O raio vai de 50 a 2000 metros.");
    setSaving(true);
    try {
      await saveStoreLocationApi(token, storeId, { latitude, longitude, radiusMeters });
      setHasSaved(true);
      toast.success("Posição da loja salva.");
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a posição da loja.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await saveStoreLocationApi(token, storeId, null);
      setLat("");
      setLng("");
      setRadius("300");
      setHasSaved(false);
      setConfirmRemove(false);
      toast.success("Posição da loja removida.");
    } catch (error: any) {
      toast.error(error?.message || "Erro ao remover a posição da loja.");
    } finally {
      setSaving(false);
    }
  };

  const searchCep = async () => {
    if (cep.replace(/\D/g, "").length !== 8) return toast.error("Informe o CEP com 8 dígitos.");
    setSearching(true);
    try {
      const result = await locateStoreByCepApi(token, cep, numero.trim() || undefined);
      setLat(result.latitude.toFixed(6));
      setLng(result.longitude.toFixed(6));
      setFound(result);
      toast.info("Posição encontrada. Confira no mapa e salve para gravar.");
    } catch (error: any) {
      setFound(null);
      toast.error(error?.message || "Erro ao buscar o CEP.");
    } finally {
      setSearching(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Este navegador não informa a localização.");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        setLocating(false);
        toast.info(`Posição capturada (precisão de ${Math.round(pos.coords.accuracy)} m). Salve para gravar.`);
      },
      () => {
        setLocating(false);
        toast.error("Não foi possível obter a localização. Permita o acesso no navegador.");
      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  return (
    <form className="storeTab__form" onSubmit={save}>
      <p className="dpFormHint">
        Usada pela avaliação de loja: com "Exigir que o supervisor esteja na loja" ligado (Administração → Configurações), a foto de
        entrada e o envio só são aceitos dentro do raio. Sem posição, a distância não é conferida.
      </p>
      {!hasSaved ? <p className="dpNotice dpNotice--warning">Loja sem posição cadastrada.</p> : null}

      <div className="dpFormGrid">
        <div className="dpFormGroup">
          <label htmlFor="storeCep">CEP</label>
          <input
            id="storeCep"
            className="dpInput"
            inputMode="numeric"
            placeholder="00000-000"
            value={cep}
            onChange={(e) => setCep(maskCep(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                searchCep();
              }
            }}
            disabled={saving || searching}
          />
        </div>
        <div className="dpFormGroup">
          <label htmlFor="storeNumero">Número</label>
          <input
            id="storeNumero"
            className="dpInput"
            placeholder="100"
            maxLength={20}
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                searchCep();
              }
            }}
            disabled={saving || searching}
          />
        </div>
        <div className="dpFormGroup">
          <label>&nbsp;</label>
          <button type="button" className="ecoBtn" disabled={saving || searching} onClick={searchCep}>
            {searching ? "Buscando..." : "Buscar pelo CEP"}
          </button>
        </div>
      </div>
      {found ? (
        <p className={`dpNotice ${found.precision === "NUMERO" ? "" : "dpNotice--warning"}`}>
          <strong>{found.address}</strong> — {PRECISION_HINT[found.precision]}
        </p>
      ) : null}

      <div className="dpFormGrid">
        <div className="dpFormGroup">
          <label htmlFor="storeLat">Latitude</label>
          <input id="storeLat" className="dpInput" inputMode="decimal" placeholder="-22.906800" value={lat} onChange={(e) => setLat(e.target.value)} disabled={saving} />
        </div>
        <div className="dpFormGroup">
          <label htmlFor="storeLng">Longitude</label>
          <input id="storeLng" className="dpInput" inputMode="decimal" placeholder="-43.172900" value={lng} onChange={(e) => setLng(e.target.value)} disabled={saving} />
        </div>
        <div className="dpFormGroup">
          <label htmlFor="storeRadius">Raio (metros)</label>
          <input id="storeRadius" type="number" min={50} max={2000} className="dpInput" value={radius} onChange={(e) => setRadius(e.target.value)} disabled={saving} />
        </div>
      </div>

      <div className="storeTab__actions storeTab__actions--split">
        <div className="storeTab__actionsGroup">
          <button type="button" className="ecoBtn ecoBtn--sm" disabled={saving || locating} onClick={useMyLocation}>
            {locating ? "Obtendo..." : "Usar minha localização atual"}
          </button>
          {lat && lng ? (
            <a
              className="ecoBtn ecoBtn--sm"
              href={`https://www.google.com/maps?q=${lat.replace(",", ".")},${lng.replace(",", ".")}`}
              target="_blank"
              rel="noreferrer"
            >
              Ver no mapa
            </a>
          ) : null}
          {hasSaved ? (
            <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--danger" disabled={saving} onClick={() => setConfirmRemove(true)}>
              Remover posição
            </button>
          ) : null}
        </div>
        <button type="submit" className="ecoBtn ecoBtn--primary" disabled={saving}>
          {saving ? "Salvando..." : "Salvar posição"}
        </button>
      </div>
      <p className="dpFormHint">
        Informe o CEP e o número e clique em "Buscar pelo CEP": a latitude e a longitude são preenchidas para você conferir no mapa e salvar.
        Se o ponto ficar longe da loja, ajuste os campos, use "minha localização atual" estando dentro da loja ou copie as coordenadas do
        Google Maps (clique com o botão direito no ponto).
      </p>

      <EcoConfirmDialog
        open={confirmRemove}
        title="Remover a posição da loja?"
        message="Sem posição, a avaliação de loja deixa de conferir a distância nesta loja."
        confirmLabel="Remover"
        tone="danger"
        busy={saving}
        onConfirm={remove}
        onCancel={() => setConfirmRemove(false)}
      />
    </form>
  );
}
